/* Trolla-verkstan — browse basic magic tricks with steps + practice log. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, Star, ChevronRight, ArrowLeft, Repeat, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Trolla() {
  const [data, setData] = useState({ tricks: [], categories: [], levels: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [cat, setCat] = useState('all');
  const [level, setLevel] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/trolla/tricks`, { withCredentials: true });
      setData(d);
    } catch { toast.error('Kunde inte ladda trick'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const logRep = async (learned) => {
    if (!selected) return;
    try {
      const { data: r } = await axios.post(`${API}/trolla/practice`,
        { trick_id: selected.id, learned: learned },
        { withCredentials: true });
      toast.success(learned ? 'Klar! Du kan detta 🎩' : `Övat ${r.reps} gånger 💜`);
      await load();
      setSelected((s) => ({ ...s, stats: { ...s.stats, reps: r.reps, learned: r.learned } }));
    } catch { toast.error('Kunde inte spara'); }
  };

  const tricks = data.tricks.filter((t) =>
    (cat === 'all' || t.category === cat) &&
    (level === 'all' || t.difficulty === level)
  );
  const learnedCount = data.tricks.filter((t) => t.stats?.learned).length;

  if (loading) {
    return <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Laddar tricken…</div>;
  }

  if (selected) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="trolla-detail">
        <button onClick={() => setSelected(null)} data-testid="trolla-back"
          className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 inline-flex items-center gap-1 hover:bg-slate-50">
          <ArrowLeft className="w-3 h-3" /> Tillbaka
        </button>

        <div className="rounded-3xl p-5 border" style={{ background: 'linear-gradient(135deg, #F4EEFF 0%, #FFF0F5 100%)', borderColor: '#DDD6FE' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase font-semibold tracking-widest text-violet-800/70">
                {data.categories.find((c) => c.id === selected.category)?.emoji}{' '}
                {data.categories.find((c) => c.id === selected.category)?.label}
              </div>
              <h1 className="text-2xl font-bold text-violet-950" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
                {selected.title}
              </h1>
              <p className="text-sm text-violet-900/80 mt-2 italic">&ldquo;{selected.hook}&rdquo;</p>
            </div>
            <div className="text-right text-xs text-violet-800">
              <div>{selected.wow_factor}</div>
              <div className="mt-1">{selected.duration_min} min</div>
              <div className="mt-1">Svår: {'★'.repeat(selected.difficulty)}{'☆'.repeat(3 - selected.difficulty)}</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-100 p-4">
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">Vad du behöver</div>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {selected.props.map((p, i) => (
              <span key={i} className="text-xs rounded-full px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-700">
                {p}
              </span>
            ))}
          </div>

          <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">Steg för steg</div>
          <ol className="space-y-2 mb-4">
            {selected.method.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" data-testid={`trolla-step-${i}`}>
                <div className="w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 text-slate-900">{s}</div>
              </li>
            ))}
          </ol>

          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-3">
            <div className="text-[10px] uppercase font-bold tracking-wider text-amber-800 mb-1">Vad du säger</div>
            <p className="text-sm text-amber-900 italic">&ldquo;{selected.patter}&rdquo;</p>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
            <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-800 mb-1">Öva-tips</div>
            <p className="text-sm text-emerald-900">💡 {selected.practice_tip}</p>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-violet-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1">
              <div className="text-xs font-semibold text-slate-800">Din framgång</div>
              <div className="text-[11px] text-slate-500">Övat {selected.stats?.reps || 0} gånger{selected.stats?.learned ? ' · Lärt in ✨' : ''}</div>
            </div>
            {selected.stats?.learned && <Award className="w-5 h-5 text-amber-500" />}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => logRep(false)}
              data-testid="trolla-log-rep"
              className="text-xs px-3 py-2 rounded-full bg-white border border-violet-300 text-violet-800 hover:bg-violet-50 inline-flex items-center gap-1">
              <Repeat className="w-3 h-3" /> +1 övning
            </button>
            {!selected.stats?.learned && (
              <button
                onClick={() => logRep(true)}
                data-testid="trolla-mark-learned"
                className="text-xs px-3 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Jag kan det!
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="trolla-page">
      <div className="rounded-3xl p-5 border relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #F4EEFF 0%, #FFF0F5 100%)', borderColor: '#DDD6FE' }}>
        <div className="absolute -right-4 -top-4 opacity-20 text-7xl leading-none pointer-events-none">🎩</div>
        <div className="text-[11px] uppercase font-semibold tracking-widest text-violet-800/70">Trolla-verkstan</div>
        <h1 className="text-2xl font-bold text-violet-950" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
          Lär dig trolla 🎩✨
        </h1>
        <p className="text-sm text-violet-900/80 mt-1">
          Basic-nivå — 10 klassiska trick med steg, replik och övnings-tips.
        </p>
        <div className="text-[11px] text-violet-800/70 mt-2">
          <Award className="w-3 h-3 inline mr-1" /> {learnedCount} / {data.tricks.length} inlärda
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="trolla-categories">
        <button onClick={() => setCat('all')}
          className={`text-xs px-2.5 py-1 rounded-full border ${cat === 'all' ? 'bg-violet-500 border-violet-500 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
          Alla ({data.tricks.length})
        </button>
        {data.categories.map((c) => {
          const count = data.tricks.filter((t) => t.category === c.id).length;
          if (count === 0) return null;
          return (
            <button key={c.id} onClick={() => setCat(c.id)}
              data-testid={`trolla-cat-${c.id}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${cat === c.id ? 'bg-violet-500 border-violet-500 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
              {c.emoji} {c.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Level filter */}
      <div className="flex flex-wrap gap-1.5" data-testid="trolla-levels">
        <button onClick={() => setLevel('all')}
          className={`text-[11px] px-2 py-0.5 rounded-full border ${level === 'all' ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
          Alla nivåer
        </button>
        {(data.levels || []).map((l) => {
          const count = data.tricks.filter((t) => t.difficulty === l.id).length;
          if (count === 0) return null;
          return (
            <button key={l.id} onClick={() => setLevel(l.id)}
              data-testid={`trolla-level-${l.id}`}
              className={`text-[11px] px-2 py-0.5 rounded-full border ${level === l.id ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
              {l.emoji} {l.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {tricks.map((t) => (
          <button key={t.id} onClick={() => setSelected(t)}
            data-testid={`trolla-open-${t.id}`}
            className="text-left rounded-2xl bg-white border border-violet-100 p-4 hover:shadow-md hover:border-violet-300 transition">
            <div className="flex items-start justify-between">
              <div className="text-2xl">{data.categories.find((c) => c.id === t.category)?.emoji || '✨'}</div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                {t.duration_min} min
              </div>
            </div>
            <div className="mt-2 text-base font-bold text-slate-900 flex items-center gap-1.5">
              {t.title}
              {t.stats?.learned && <Check className="w-3.5 h-3.5 text-emerald-500" />}
            </div>
            <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{t.hook}</p>
            <div className="flex items-center justify-between mt-2">
              <div className="text-[10px] text-amber-500">
                {'★'.repeat(t.difficulty)}<span className="text-slate-300">{'★'.repeat(3 - t.difficulty)}</span>
              </div>
              <div className="text-[11px] text-slate-400 flex items-center gap-1">
                {t.stats?.reps > 0 && <span>{t.stats.reps}× övat</span>}
                <ChevronRight className="w-3 h-3" />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
